import { PencilLine, Save, Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import api from "../../../services/axios";
import { toast } from "react-hot-toast";

// Card component
const ConfigCard = ({
  title,
  total,
  stationData,
  options,
  onEditLabel,
  isEditable = true,
  onSave,
  setunsavedChanges,
  showError
}) => {
  const [editModal, setEditModal] = useState({
    show: false,
    id: null,
    title: "",
    original_title: "",
  });

  const [checkedOptions, setCheckedOptions] = useState(
    options.map((opt) => opt.checked)
  );
  const [aggregationOptions, setAggregationOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasUserInteraction, setHasUserInteraction] = useState(false);
  const [optionsKey, setOptionsKey] = useState(JSON.stringify(options.map(opt => ({ id: opt.id, checked: opt.checked }))));
  
  // Store original state for comparison
  const [originalCheckedOptions, setOriginalCheckedOptions] = useState(
    options.map((opt) => opt.checked)
  );
  const [originalAggregationOptions, setOriginalAggregationOptions] = useState([]);
  
  // Track previous hasChanges value to prevent infinite loops
  const prevHasChangesRef = useRef(null);

  // Update aggregation options when options prop changes (only on first load)
  useEffect(() => {
    if (!isInitialized) {
      const processedAggregationOptions = options.map((option) => {
        const aggregationState = {};
        
        // Initialize all aggregation types from the option data
        if (option.aggregation_types && Array.isArray(option.aggregation_types)) {
          option.aggregation_types.forEach((aggType) => {
            if (typeof aggType === 'object') {
              // Handle the format: [{type: boolean}, {type: boolean}]
              Object.keys(aggType).forEach(key => {
                aggregationState[key] = aggType[key];
              });
            }
          });
        }
        
        // If this sensor has only one aggregation type, ensure it's checked and disabled
        if (option.has_single_aggregation && option.available_aggregation_types) {
          option.available_aggregation_types.forEach(type => {
            aggregationState[type] = true;
          });
        }
        
        return aggregationState;
      });
      
      setAggregationOptions(processedAggregationOptions);
      // Store original aggregation options
      setOriginalAggregationOptions(JSON.parse(JSON.stringify(processedAggregationOptions)));
      setIsInitialized(true);
    }
  }, [options, isInitialized]);

  // Update checked options when options prop changes (only if user hasn't interacted yet or after save)
  useEffect(() => {
    const newOptionsKey = JSON.stringify(options.map(opt => ({ id: opt.id, checked: opt.checked })));
    
    if (!hasUserInteraction || newOptionsKey !== optionsKey) {
      const newCheckedOptions = options.map((opt) => opt.checked);
      setCheckedOptions(newCheckedOptions);
      // Update original state when options come from API
      setOriginalCheckedOptions(newCheckedOptions);
      
      // Also update original aggregation options
      const processedAggregationOptions = options.map((option) => {
        const aggregationState = {};
        
        if (option.aggregation_types && Array.isArray(option.aggregation_types)) {
          option.aggregation_types.forEach((aggType) => {
            if (typeof aggType === 'object') {
              Object.keys(aggType).forEach(key => {
                aggregationState[key] = aggType[key];
              });
            }
          });
        }
        
        if (option.has_single_aggregation && option.available_aggregation_types) {
          option.available_aggregation_types.forEach(type => {
            aggregationState[type] = true;
          });
        }
        
        return aggregationState;
      });
      setOriginalAggregationOptions(JSON.parse(JSON.stringify(processedAggregationOptions)));
      
      // Reset the ref since original state matches current state
      prevHasChangesRef.current = false;
      
      setOptionsKey(newOptionsKey);
    }
  }, [options, hasUserInteraction, optionsKey]);

  // Get all available aggregation types for a specific sensor
  const getAvailableAggregationTypes = (optionIndex) => {
    const option = options[optionIndex];
    
    // Use the available_aggregation_types if provided, otherwise extract from aggregation_types
    if (option?.available_aggregation_types) {
      return option.available_aggregation_types;
    }
    
    const availableTypes = new Set();
    if (option?.aggregation_types && Array.isArray(option.aggregation_types)) {
      option.aggregation_types.forEach((aggType) => {
        if (typeof aggType === 'object') {
          Object.keys(aggType).forEach(key => {
            availableTypes.add(key);
          });
        }
      });
    }
    
    return Array.from(availableTypes);
  };

  // Check if a specific aggregation type should be disabled
  const isAggregationTypeDisabled = (optionIndex, aggregationType) => {
    const option = options[optionIndex];
    
    // If sensor has only one aggregation type, disable it
    if (option?.has_single_aggregation === true) {
      return true;
    }
    
    // If sensor has multiple aggregation types, check if this is the last selected one
    const availableTypes = getAvailableAggregationTypes(optionIndex);
    const currentAggregationState = aggregationOptions[optionIndex] || {};
    
    // Count how many aggregation types are currently selected
    const selectedCount = availableTypes.filter(type => currentAggregationState[type] === true).length;
    
    // If only one is selected and this is that one, disable it
    if (selectedCount === 1 && currentAggregationState[aggregationType] === true) {
      return true;
    }
    
    return false;
  };

  const checkedCount = checkedOptions.filter(Boolean).length;
  const isAllChecked = checkedCount === options.length;
  const isIndeterminate = checkedCount > 0 && checkedCount < options.length;

  // Compare current state with original state and update unsavedChanges
  useEffect(() => {
    if (!setunsavedChanges || !isInitialized) return;
    
    // Compare checked options
    const checkedOptionsChanged = JSON.stringify(checkedOptions) !== JSON.stringify(originalCheckedOptions);
    
    // Compare aggregation options
    const aggregationOptionsChanged = JSON.stringify(aggregationOptions) !== JSON.stringify(originalAggregationOptions);
    
    const hasChanges = checkedOptionsChanged || aggregationOptionsChanged;
    
    // Only update if the value actually changed to prevent infinite loops
    if (prevHasChangesRef.current === hasChanges) {
      return;
    }
    
    prevHasChangesRef.current = hasChanges;
    
    setunsavedChanges((prev) => ({
      ...prev,
      [stationData.station_id]: hasChanges,
    }));
  }, [checkedOptions, aggregationOptions, originalCheckedOptions, originalAggregationOptions, stationData.station_id, isInitialized]);

  const handleHeaderCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setHasUserInteraction(true);
    setCheckedOptions(new Array(options.length).fill(isChecked));
  };

  const handleOptionCheckboxChange = (index) => {
    setHasUserInteraction(true);
    setCheckedOptions((prev) =>
      prev.map((checked, i) => (i === index ? !checked : checked))
    );
  };

  const handleAggregationCheckboxChange = (sensorIndex, aggregationType) => {
    setHasUserInteraction(true);
    
    setAggregationOptions((prev) => {
      const currentAggregationState = prev[sensorIndex] || {};
      const availableTypes = getAvailableAggregationTypes(sensorIndex);
      
      // Count how many aggregation types are currently selected
      const selectedCount = availableTypes.filter(type => currentAggregationState[type] === true).length;
      
      // Prevent deselection if this is the last remaining selected aggregation type
      const isCurrentlySelected = currentAggregationState[aggregationType] === true;
      if (isCurrentlySelected && selectedCount === 1) {
        // Don't allow deselection of the last one
        return prev;
      }
      
      // Allow the change
      return prev.map((agg, i) =>
        i === sensorIndex
          ? { ...agg, [aggregationType]: !(agg?.[aggregationType] || false) }
          : agg
      );
    });
  };

  const handleSave = async () => {
    // console.log(first)
    console.log(checkedOptions.filter((i) => i.checked).map((s) => s.id));
    try {
      setSaving(true);
      const checkedSensorIds = options
        .filter((_, idx) => checkedOptions[idx])
        .map((opt) => opt.id);

      const uncheckedSensorIds = options
        .filter((_, idx) => !checkedOptions[idx])
        .map((opt) => opt.id);

      // Prepare aggregation data for all sensors (both checked and unchecked)
      const sensorAggregationData = options.map((opt, idx) => {
        const selectedAggregations = Object.keys(aggregationOptions[idx] || {})
          .filter(key => aggregationOptions[idx][key]);
        
        return {
          sensor_id: opt.id,
          aggregation_types: selectedAggregations
        };
      }).filter(item => item.aggregation_types.length > 0); // Only include sensors with selected aggregations

      await onSave?.(checkedSensorIds, uncheckedSensorIds, sensorAggregationData);
      
      // Update original state to match current state after successful save
      setOriginalCheckedOptions([...checkedOptions]);
      setOriginalAggregationOptions(JSON.parse(JSON.stringify(aggregationOptions)));
      
      // Reset the ref since state is now clean
      prevHasChangesRef.current = false;
      
      if(setunsavedChanges){
        setunsavedChanges((prev) => ({
          ...prev,
          [stationData.station_id]: false,
        }));
      }
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    // const res = await api.put(`config/sensors/${editModal.id}`, {user_defined_title:editModal.title})
    await onEditLabel(editModal.id, editModal.title, editModal.original_title);
    toast.success(`Updated successfully!`);
    setEditModal({ show: false, id: null, title: "" });
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="h-full flex flex-col rounded-md shadow border border-gray-200 dark:border-gray-700 overflow-hidden bg-base-100 dark:bg-gray-800 max-w-md min-w-80 transition-colors duration-200">
        {/* Card Header */}
        <div className="flex justify-between items-center bg-[#DDDDDD] dark:bg-gray-700 border-2 border-white dark:border-gray-800 rounded-md rounded-b-none px-4 py-4 transition-colors duration-200">
          <div className="flex items-center gap-2 cursor-pointer font-normal text-sm text-black dark:text-white">
            <input
              type="checkbox"
              className="checkbox checkbox-sm checkbox-secondary rounded dark:border-gray-600"
              checked={isAllChecked}
              ref={(el) => {
                if (el) el.indeterminate = isIndeterminate;
              }}
              onChange={isEditable ? handleHeaderCheckboxChange : null}
              id={title.trim()}
            />
            <div className="flex flex-col">
              <label htmlFor={title.trim()} className="cursor-pointer">
                {title}
              </label>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                {/* <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 dark:bg-blue-600 transition-all duration-300"
                    style={{
                      width: `${(checkedCount / options.length) * 100}%`,
                    }}
                  />
                </div> */}
                <span className="text-[10px]">
                  {checkedCount}/{options.length} selected
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditable && (
              <button
                className="btn btn-ghost btn-xs hover:bg-gray-100 dark:hover:bg-gray-600 p-1"
                onClick={handleSave}
                disabled={saving}
                title={saving ? "Saving..." : "Save configuration"}
              >
                {saving ? (
                  <Loader2
                    size={14}
                    className="text-black dark:text-gray-300 animate-spin"
                  />
                ) : (
                  <Save size={14} className="text-black dark:text-gray-300" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Card Body */}
        <div className="flex-1 my-4 space-y-3 max-h-72 overflow-y-auto px-4 bg-[#F3F5F7] dark:bg-gray-800 transition-colors duration-200">
          {options.map((opt, idx) => (
            <div key={idx} className="bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm border border-gray-100 dark:border-gray-600">
              {/* Sensor Name Row */}
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-800 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs checkbox-secondary rounded font-thin dark:border-gray-600"
                    checked={checkedOptions[idx]}
                    onChange={
                      isEditable ? () => handleOptionCheckboxChange(idx) : null
                    }
                  />
                  <span className="font-medium">{opt.label}</span>
                </label>
                {isEditable && (
                  <button
                    className="btn btn-ghost btn-xs hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() =>
                      setEditModal({
                        show: true,
                        id: opt.id,
                        title: opt.label,
                        original_title: opt.original_title,
                      })
                    }
                  >
                    <PencilLine
                      size={14}
                      className="text-gray-500 dark:text-gray-400"
                    />
                  </button>
                )}
              </div>
              
              {/* Aggregation Types Row */}
              {checkedOptions[idx] && (
                <div className="ml-6 mt-2">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Aggregation Types:
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    {getAvailableAggregationTypes(idx).map((aggType) => {
                      const isDisabled = isAggregationTypeDisabled(idx, aggType);
                      const isChecked = aggregationOptions[idx]?.[aggType] || false;
                      
                      return (
                        <label
                          key={aggType}
                          className={`flex items-center gap-1 text-xs ${
                            isDisabled 
                              ? 'cursor-not-allowed text-gray-400 dark:text-gray-500' 
                              : 'cursor-pointer text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className={`checkbox checkbox-xs checkbox-primary rounded dark:border-gray-500 ${
                              isDisabled ? 'cursor-not-allowed opacity-50' : ''
                            }`}
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={
                              isEditable && !isDisabled
                                ? () => handleAggregationCheckboxChange(idx, aggType)
                                : null
                            }
                          />
                          <span className="text-xs capitalize">{aggType}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {showError && (
        <div className="text-sm text-red-500 dark:text-red-400">
          You have unsaved changes in {title}
        </div>
      )}
      </div>

      {/* Edit Modal */}
      {editModal.show && (
        <dialog className="modal modal-open">
          <div className="modal-box bg-white dark:bg-gray-800">
            <h3 className="font-medium text-lg text-gray-900 dark:text-gray-100">
              Change Label
            </h3>
            <div className="py-4">
              <input
                type="text"
                placeholder="Enter new title"
                value={editModal.title}
                onChange={(e) =>
                  setEditModal((prev) => ({ ...prev, title: e.target.value }))
                }
                className="input input-bordered w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-normal"
              />
            </div>
            <div className="modal-action justify-start gap-1 mt-1">
              <button
                className="btn bg-blue-600 hover:bg-blue-700 text-white border-none rounded-full px-4 py-1 capitalize text-sm font-normal h-auto min-h-max"
                onClick={() => handleEdit()}
              >
                Update
              </button>
              <button
                className="btn btn-outline bg-gray-300 border-none dark:border-gray-600 dark:bg-slate-200 dark:text-black dark:hover:bg-gray-700 rounded-full px-4 py-1 capitalize text-sm font-normal h-auto min-h-max"
                onClick={() =>
                  setEditModal({ show: false, id: null, title: "" })
                }
              >
                Cancel
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button
              onClick={() => setEditModal({ show: false, id: null, title: "" })}
            >
              close
            </button>
          </form>
        </dialog>
      )}
    </>
  );
};

export default ConfigCard;
